import React, { useState } from 'react'
import { SPEEDS, DURATIONS, getPrice } from '../data/plans'
import './Step1Form.css'

const UserIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
    <circle cx="12" cy="7" r="4"/>
  </svg>
)

const PhoneIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6.16 6.16l.91-.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
  </svg>
)

const ZapIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
  </svg>
)

const CalendarIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
    <line x1="16" y1="2" x2="16" y2="6"/>
    <line x1="8" y1="2" x2="8" y2="6"/>
    <line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
)

const ChevronDown = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true">
    <polyline points="6 9 12 15 18 9"/>
  </svg>
)

const ArrowRight = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true">
    <line x1="5" y1="12" x2="19" y2="12"/>
    <polyline points="12 5 19 12 12 19"/>
  </svg>
)

const Step1Form = ({ onNext }) => {
  const [form, setForm] = useState({ name: '', phone: '', speed: '', duration: '' })
  const [errors, setErrors] = useState({})
  const [touched, setTouched] = useState({})

  const price = getPrice(form.speed, form.duration)

  const validate = (fields = form) => {
    const errs = {}
    if (!fields.name.trim())              errs.name     = 'Full name is required'
    else if (fields.name.trim().length < 2) errs.name   = 'Enter a valid name'
    if (!fields.phone.trim())             errs.phone    = 'Phone number is required'
    else if (!/^[6-9]\d{9}$/.test(fields.phone.trim())) errs.phone = 'Enter a valid 10-digit mobile number'
    if (!fields.speed)                    errs.speed    = 'Please select a plan speed'
    if (!fields.duration)                 errs.duration = 'Please select a duration'
    return errs
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    const updated = { ...form, [name]: value }
    setForm(updated)
    if (touched[name]) {
      setErrors(prev => ({ ...prev, [name]: validate(updated)[name] }))
    }
  }

  const handleBlur = (e) => {
    const { name } = e.target
    setTouched(prev => ({ ...prev, [name]: true }))
    setErrors(prev => ({ ...prev, [name]: validate()[name] }))
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    const allTouched = { name: true, phone: true, speed: true, duration: true }
    setTouched(allTouched)
    const errs = validate()
    setErrors(errs)
    if (Object.keys(errs).length === 0) {
      onNext({ ...form, price })
    }
  }

  const isFormValid = Object.keys(validate()).length === 0

  return (
    <section className="step1" aria-labelledby="step1-heading">
      <div className="step1__card">
        <div className="step1__card-header">
          <h1 id="step1-heading" className="step1__title">Payment Details</h1>
          <p className="step1__subtitle">Enter your information to continue to payment</p>
        </div>

        <form className="step1__form" onSubmit={handleSubmit} noValidate>

          {/* Name */}
          <div className={`step1__field ${errors.name && touched.name ? 'step1__field--error' : ''} ${!errors.name && touched.name ? 'step1__field--valid' : ''}`}>
            <label htmlFor="name" className="step1__label">
              <UserIcon />
              Full Name
            </label>
            <input
              id="name"
              name="name"
              type="text"
              className="step1__input"
              placeholder="e.g. Rahul Sharma"
              value={form.name}
              onChange={handleChange}
              onBlur={handleBlur}
              autoComplete="name"
              aria-describedby={errors.name && touched.name ? 'name-error' : undefined}
              aria-invalid={errors.name && touched.name ? 'true' : undefined}
            />
            {errors.name && touched.name && (
              <span id="name-error" className="step1__error" role="alert">{errors.name}</span>
            )}
          </div>

          {/* Phone */}
          <div className={`step1__field ${errors.phone && touched.phone ? 'step1__field--error' : ''} ${!errors.phone && touched.phone ? 'step1__field--valid' : ''}`}>
            <label htmlFor="phone" className="step1__label">
              <PhoneIcon />
              Mobile Number
            </label>
            <div className="step1__phone-wrap">
              <span className="step1__phone-prefix" aria-hidden="true">+91</span>
              <input
                id="phone"
                name="phone"
                type="tel"
                className="step1__input step1__input--phone"
                placeholder="Enter 10-digit number"
                value={form.phone}
                onChange={handleChange}
                onBlur={handleBlur}
                autoComplete="tel-national"
                maxLength={10}
                inputMode="numeric"
                pattern="[6-9][0-9]{9}"
                aria-describedby={errors.phone && touched.phone ? 'phone-error' : undefined}
                aria-invalid={errors.phone && touched.phone ? 'true' : undefined}
              />
            </div>
            {errors.phone && touched.phone && (
              <span id="phone-error" className="step1__error" role="alert">{errors.phone}</span>
            )}
          </div>

          {/* Speed + Duration row */}
          <div className="step1__row">

            {/* Speed */}
            <div className={`step1__field ${errors.speed && touched.speed ? 'step1__field--error' : ''} ${!errors.speed && touched.speed ? 'step1__field--valid' : ''}`}>
              <label htmlFor="speed" className="step1__label">
                <ZapIcon />
                Plan Speed
              </label>
              <div className="step1__select-wrap">
                <select
                  id="speed"
                  name="speed"
                  className="step1__select"
                  value={form.speed}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  aria-describedby={errors.speed && touched.speed ? 'speed-error' : undefined}
                  aria-invalid={errors.speed && touched.speed ? 'true' : undefined}
                >
                  <option value="" disabled>Select speed</option>
                  {SPEEDS.map(s => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
                <span className="step1__select-arrow" aria-hidden="true"><ChevronDown /></span>
              </div>
              {errors.speed && touched.speed && (
                <span id="speed-error" className="step1__error" role="alert">{errors.speed}</span>
              )}
            </div>

            {/* Duration */}
            <div className={`step1__field ${errors.duration && touched.duration ? 'step1__field--error' : ''} ${!errors.duration && touched.duration ? 'step1__field--valid' : ''}`}>
              <label htmlFor="duration" className="step1__label">
                <CalendarIcon />
                Duration
              </label>
              <div className="step1__select-wrap">
                <select
                  id="duration"
                  name="duration"
                  className="step1__select"
                  value={form.duration}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  aria-describedby={errors.duration && touched.duration ? 'duration-error' : undefined}
                  aria-invalid={errors.duration && touched.duration ? 'true' : undefined}
                >
                  <option value="" disabled>Select duration</option>
                  {DURATIONS.map(d => (
                    <option key={d.value} value={d.value}>{d.label}</option>
                  ))}
                </select>
                <span className="step1__select-arrow" aria-hidden="true"><ChevronDown /></span>
              </div>
              {errors.duration && touched.duration && (
                <span id="duration-error" className="step1__error" role="alert">{errors.duration}</span>
              )}
            </div>

          </div>

          {/* Live amount preview */}
          {price !== null && (
            <div className="step1__amount-preview" aria-live="polite" aria-atomic="true">
              <div className="step1__amount-preview-inner">
                <div className="step1__amount-preview-left">
                  <span className="step1__amount-label">Amount to Pay</span>
                  <span className="step1__amount-note">Inclusive of all taxes</span>
                </div>
                <div className="step1__amount-value" aria-label={`Amount: ${price} rupees`}>
                  <span className="step1__amount-currency">&#8377;</span>
                  <span className="step1__amount-number">{price.toLocaleString('en-IN')}</span>
                </div>
              </div>
              {form.duration === '30' && form.speed !== 'demo' && (
                <p className="step1__install-note">
                  + &#8377;500 installation charges apply for 30-day plans
                </p>
              )}
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            className={`step1__submit ${isFormValid ? 'step1__submit--active' : ''}`}
            aria-disabled={!isFormValid}
          >
            {price !== null
              ? `Proceed to Pay \u20b9${price.toLocaleString('en-IN')}`
              : 'Select a Plan to Continue'
            }
            <ArrowRight />
          </button>

          {/* Security note */}
          <p className="step1__security-note" aria-label="Security information">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
              aria-hidden="true">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
            Your information is encrypted and secure
          </p>

        </form>
      </div>
    </section>
  )
}

export default Step1Form
